import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import { WebinarCreatorForm } from '../ui/webinar-creator-form';
import { withErrorHandling } from '@selfserve/react-hocs';
import { StyledContentContainer, SplitScreenLayout } from '@selfserve/react-ui';
import { useNavigate, useParams } from 'react-router-dom';
import { apiStatuses, routesPaths } from '@selfserve/react-utils';
import { FormikProps, useFormik } from 'formik';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { WebinarSidescreen } from '../ui/webinar-sidescreen';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useSetWebinarData, WebinarWizardData } from '../data';
import { getNextStep, isLastStep } from '../utils/webinar-steps-helper';
import { storage, storageKeys, useStorage } from '@selfserve/react-hooks';

const StyledWebinarCreatorContainer = styled.div(() => ({
  maxWidth: '560px',
  fontFamily: 'Lato',
  width: '100%',
}));

export interface SpeakerData {
  email: string;
  roles?: string[];
  firstName: string;
  lastName: string;
  title?: string;
  company?: string;
  bio?: string;
  photo?: string;
}

export interface FormData {
  name?: string;
  description?: string;
  date?: number;
  time?: string;
  duration?: string;
  timezone?: string;
  theme?: string;
  banner?: string;
  logo?: string;
  speakers: SpeakerData[];
}

const ImageUrlToBase64 = async (imageUrl: string): Promise<string> => {
  const res = await fetch(imageUrl);
  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        resolve((reader.result as string).split(',')[1]);
      },
      false
    );

    reader.onerror = () => {
      return reject(this);
    };
    reader.readAsDataURL(blob);
  });
};

const formikToProps = async (values: FormData): Promise<WebinarWizardData> => {
  const {
    name,
    description,
    theme,
    logo,
    duration,
    banner,
    date,
    time,
    speakers,
    timezone,
  } = values;
  return {
    name,
    description,
    duration,
    time,
    timezone,
    date: moment(date).format('MM-DD-YY'),

    branding: {
      theme,
      image: await ImageUrlToBase64(banner as string),
      logo: await ImageUrlToBase64(logo as string),
    },

    speakers: await Promise.all(
      speakers.map(async (speaker) => ({
        ...speaker,
        image: !speaker.photo
          ? ''
          : await ImageUrlToBase64(speaker.photo as string),
      }))
    ),
  };
};

const StyledLoadingScreen = styled.div(({ theme }: { theme?: any }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: 'white',
  zIndex: '10',
  display: 'flex',
  verticalAlign: 'middle',
  justifyContent: 'center',
  flexDirection: 'column',
  '& video': {
    height: '300px',
  },
  '& p': {
    textAlign: 'center',
    fontSize: '20px',
    lineHeight: '22px',
    fontWeight: '700',
    fontFamily: 'Lato',
  },
}));

export const WebinarCreatorWizard = withErrorHandling(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionId, stepName = '' } = useParams();
  const { mutateAsync: handleUpdateRequest, data: updateData } =
    useSetWebinarData(subscriptionId as string);
  const [isPreparedToSend, setIsPreparedToSend] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [sendData, setSendData] = useState<WebinarWizardData | null>(null);
  const initialTime = moment();
  const roundupMinutes = 30 - (initialTime.minute() % 30);
  const [savedData, setSavedData] = useStorage(
    storage.LOCAL,
    storageKeys.WEBINAR_WIZARD + subscriptionId
  );

  const validationSchema = (t: TFunction) =>
    Yup.object().shape({
      name: Yup.mixed().required(
        t('webinar-wizard.validations.title-required')
      ),
    });

  const speakers = [
    {
      email: 'test1@test.com',
      roles: ['speaker'],
      firstName: 'Roger',
      lastName: 'Chalmer',
      title: 'CEO',
      company: 'Company name',
      bio: 'Bio',
      photo: 'assets/speaker_1.png',
    },
    {
      email: 'test2@test.com',
      roles: ['speaker'],
      firstName: 'Dona',
      lastName: 'Williams',
      title: 'CEO',
      company: 'Company name',
      bio: 'Bio',
      photo: 'assets/speaker_2.png',
    },
    {
      email: 'test3@test.com',
      roles: ['speaker'],
      firstName: 'Helen',
      lastName: 'Patrick',
      title: 'VP of Customer Success',
      company: 'Company name',
      bio: 'Bio',
      photo: 'assets/speaker_3.png',
    },
    {
      email: 'test4@test.com',
      roles: ['speaker'],
      firstName: 'Constance',
      lastName: 'Chen',
      title: 'VP Product',
      company: 'Company name',
      bio: 'Bio',
      photo: 'assets/speaker_4.png',
    },
  ];

  const initialValues: FormData = {
    name: t('webinar-wizard.title-default'),
    description: t('webinar-wizard.description-default'),
    date: initialTime.valueOf(),
    time: initialTime.add(roundupMinutes, 'minutes').format('hh:mm a'),
    duration: '30',
    theme: 'dark',
    logo: 'assets/branding_logo.svg',
    banner: 'assets/branding_banner.png',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    speakers: speakers,
  };

  const onSubmit = () => {
    if (isLastStep(stepName)) {
      setIsPreparedToSend(true);
      setLoading(true);
      return;
    }

    navigate(
      '../' +
        routesPaths.WEBINAR_WIZARD.replace(
          ':stepName',
          getNextStep(stepName)
        ).replace(':subscriptionId', subscriptionId as string)
    );
  };

  if (!savedData) {
    setSavedData(initialValues);
  }

  const formik: FormikProps<FormData> = useFormik<FormData>({
    initialValues: savedData,
    validationSchema: validationSchema(t),
    onSubmit: onSubmit,
  });

  const values = formik.values;
  useEffect(() => {
    setSavedData(values);
  }, [values]);

  useEffect(() => {
    const initFormatting = async (values: FormData) => {
      const newValues = await formikToProps(formik.values);
      setSendData(newValues);
    };

    if (isPreparedToSend) {
      initFormatting(formik.values);
      setIsPreparedToSend(false);
    }
  }, [isPreparedToSend, formik, setSendData]);

  useEffect(() => {
    if (sendData) {
      handleUpdateRequest(sendData);
      setSendData(null);
    }
  }, [handleUpdateRequest, sendData]);

  useEffect(() => {
    if (updateData?.status && updateData?.status === apiStatuses.CREATED) {
      window.open(updateData.data?.webinarHostName, '_blank');
      window.location.href = updateData.data?.epAdminPage;
    }
  }, [updateData, navigate]);

  return (
    <>
      <Helmet>
        <title>{t('titles.marketing-info')}</title>
      </Helmet>

      {isLoading && (
        <StyledLoadingScreen>
          <video autoPlay loop muted>
            <source src="assets/request_processing.mp4" type="video/mp4" />
          </video>
          <p>{t('webinar-wizard.creating-message')}</p>
        </StyledLoadingScreen>
      )}
      <SplitScreenLayout
        secondScreen={<WebinarSidescreen t={t} formik={formik} />}
      >
        <StyledContentContainer centered>
          <StyledWebinarCreatorContainer>
            <WebinarCreatorForm
              isLoading={isLoading}
              stepName={stepName}
              formik={formik}
            />
          </StyledWebinarCreatorContainer>
        </StyledContentContainer>
      </SplitScreenLayout>
    </>
  );
});
